import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
} from '@mui/material';
import { FieldTextRequired } from '../../form';
import { useForm } from 'react-typed-form';
import { useApiCallable } from '../../api';
import { ApiError } from '../../api/sustain-api-request';
import { ErrorList } from '../../components';
import { useNavigate } from 'react-router';
import React from 'react';

type Props = {
  email: string;
};

type ForgotPasswordForm = {
  email: string;
};

export default function ForgotPasswordForm({ email: initialEmail }: Props) {
  const callApi = useApiCallable();
  const navigate = useNavigate();
  const [emailSent, setEmailSent] = React.useState(false);

  const form = useForm<ForgotPasswordForm>({
    defaultValues: {
      email: initialEmail,
    },
    onSubmit: async (values, { setLoading }) => {
      setLoading(true);
      const response = await callApi<any, any>(
        '/reset-password/request',
        {
          method: 'POST',
          jsonBody: { email: values.email },
        },
        {
          errorHandler: (error, originalHandler) => {
            if (error instanceof ApiError && error.response.status === 400) {
              form.addSubmitError('_form', error.response.data.error);
            }
            originalHandler(error);
          },
        },
      );

      setLoading(false);

      if (response) {
        setEmailSent(true);
      }
    },
  });

  if (emailSent) {
    return (
      <Card>
        <CardHeader title="Please check your inbox" />
        <CardContent>
          <Stack spacing={2}>
            <Typography>
              We've sent an email to the address provided. Can't find it? Check
              your junk folder.
            </Typography>
            <Typography>The link in the email isn't working?</Typography>
            <Button
              variant="outlined"
              onClick={() =>
                navigate(
                  `/forgot-password/manual-entry?email=${encodeURIComponent(form.values.email)}`,
                )
              }
            >
              Enter code manually
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault();
        form.handleSubmit();
      }}
    >
      <Card>
        <CardHeader title="Reset your password" />
        <CardContent>
          <Stack spacing={2}>
            <Typography>
              We will send you an email with a link to create a new password.
            </Typography>
            <FieldTextRequired
              label="Email address"
              field={form.getField('email')}
              type="email"
            />
            <Button variant="contained" type="submit" disabled={form.isLoading}>
              Send Reset Link
            </Button>
            <Button variant="text" onClick={() => navigate('/login')}>
              Go Back
            </Button>
            <ErrorList errors={form.lastErrors._form} />
          </Stack>
        </CardContent>
      </Card>
    </form>
  );
}
